import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Flag, Check, X, AlertTriangle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const AdminReports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  useEffect(() => {
    fetchReports();
  }, [filter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await api.getAdminReports(filter || undefined);
      setReports(response.data);
    } catch (error) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (reportId, status) => {
    try {
      await api.updateReport(reportId, status);
      toast.success(`Report marked as ${status}`);
      fetchReports();
    } catch (error) {
      toast.error("Failed to update report");
    }
  };

  const handleSuspendUser = async (userId) => {
    try {
      await api.updateAdminUser(userId, { is_suspended: true });
      toast.success("User suspended");
    } catch (error) {
      toast.error("Failed to suspend user");
    }
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-700",
    reviewed: "bg-blue-100 text-blue-700",
    resolved: "bg-green-100 text-green-700",
    dismissed: "bg-gray-100 text-gray-700"
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
    <div className="bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4 sticky top-[52px] z-10">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="text-xl font-bold text-gray-900">Reports</h1>
        </div>
        
        <div className="flex gap-2 overflow-x-auto pb-1">
          {['pending', 'reviewed', 'resolved', 'dismissed', ''].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filter === status
                  ? 'bg-rose-500 text-white'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              {status || 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-rose-500 border-t-transparent"></div>
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Flag className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No reports found</h3>
          <p className="text-gray-600">All clear!</p>
        </div>
      ) : (
        <div className="p-4 space-y-4">
          {reports.map((report) => (
            <div key={report.id} className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[report.status]}`}>
                    {report.status}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  {formatDate(report.created_at)}
                </div>
              </div>
              
              <div className="mb-3">
                <p className="text-sm text-gray-500">Reason</p>
                <p className="font-medium text-gray-900">{report.reason}</p>
                {report.details && (
                  <p className="text-sm text-gray-600 mt-1">{report.details}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Reporter</p>
                  <p className="font-medium text-gray-900 text-sm truncate">
                    {report.reporter?.name || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{report.reporter?.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Reported User</p>
                  <p className="font-medium text-gray-900 text-sm truncate">
                    {report.reported_user?.name || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{report.reported_user?.email}</p>
                </div>
              </div>
              
              {report.status === 'pending' && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUpdateStatus(report.id, 'dismissed')}
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-1" /> Dismiss
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUpdateStatus(report.id, 'reviewed')}
                    className="flex-1"
                  >
                    Review
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      handleSuspendUser(report.reported_user_id);
                      handleUpdateStatus(report.id, 'resolved');
                    }}
                    className="flex-1 bg-red-500 hover:bg-red-600"
                  >
                    Suspend
                  </Button>
                </div>
              )}
              
              {report.status === 'reviewed' && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUpdateStatus(report.id, 'dismissed')}
                    className="flex-1"
                  >
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      handleSuspendUser(report.reported_user_id);
                      handleUpdateStatus(report.id, 'resolved');
                    }}
                    className="flex-1 bg-red-500 hover:bg-red-600"
                  >
                    Suspend & Resolve
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminReports;
